import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  Button
} from '@mui/material';
import ManufacturingFloorVisualization from '../components/ManufacturingFloorVisualization';
import { 
  FaEye, 
  FaChartLine, 
  FaIndustry 
} from 'react-icons/fa';
import { 
  MdPrecisionManufacturing, 
  MdTrendingUp, 
  MdVisibility 
} from 'react-icons/md';
import { 
  GiFactory 
} from 'react-icons/gi';
import { 
  BiCameraMovie 
} from 'react-icons/bi';
import { 
  HiOutlineEye 
} from 'react-icons/hi';
import marketingTheme from '../themes/marketingTheme';
import { 
  RiLineChartLine 
} from 'react-icons/ri';

const Manufacturing = () => {
  const benefits = [
    {
      icon: <HiOutlineEye size={36} />,
      title: 'Advanced Visual Analysis',
      description: 'Gain comprehensive insights into your manufacturing processes through AI-powered computer vision'
    },
    {
      icon: <GiFactory size={36} />,
      title: 'Process Optimization',
      description: 'Identify and eliminate inefficiencies in your production line with spatial intelligence'
    },
    {
      icon: <RiLineChartLine size={36} />,
      title: 'Data-Driven Decisions',
      description: 'Make informed decisions based on real-time visual analytics and production metrics'
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#24242a', minHeight: '100vh', pt: 12 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h1" sx={{ 
            color: 'white', 
            mb: 2,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            fontWeight: 700
          }}>
            Manufacturing Solutions
          </Typography>
          <Typography variant="h5" sx={{ 
            color: '#edb92e', 
            mb: 4,
            fontWeight: 400
          }}>
            Vision AI for industrial productivity & safety.
          </Typography>
        </Box>

        {/* 3D Manufacturing Floor Visualization */}
        <Box sx={{ 
          mb: 8,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          // background: 'linear-gradient(135deg, rgba(237, 185, 46, 0.1) 0%, rgba(235, 77, 75, 0.1) 100%)',
          // border: '1px solid rgba(237, 185, 46, 0.3)',
          // grey with edb92e border
          backgroundColor: 'rgba(36, 36, 42, 0.8)',
          border: '1px solid rgba(237, 185, 46, 0.3)',
        }}>
          <Grid container spacing={6} alignItems="center" sx={{ p: 6, position: 'relative', zIndex: 1 }}>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h4" sx={{ 
                color: 'white', 
                mb: 3,
                fontWeight: 600
              }}>
                See Your Production Floor Like Never Before
              </Typography>
              {/* Process Segmentation Image */}
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <img 
                  src="/images/process_segmentation.png" 
                  alt="AI Vision for Production Flow" 
                  style={{ 
                    // Centre the image
                    display: 'block',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    maxWidth: '1200px',
                    width: '100%',
                    height: 'auto',
                    borderRadius: '8px'
                  }} 
                />
              </Box>
              
              {/* Key Platform Benefits */}
                {benefits.map((benefit, index) => (
                  <Box 
                    key={index}
                    sx={{
                      position: 'relative',
                      mb: index === benefits.length - 1 ? 0 : 3,
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 30px -10px rgba(237, 185, 46, 0.2)',
                        '& .benefit-icon-wrapper': {
                          transform: 'scale(1.1)',
                        },
                        '& .benefit-highlight': {
                          width: '100%',
                          opacity: 0.1,
                        }
                      }
                    }}
                  >
                    {/* Highlight bar that expands on hover */}
                    <Box 
                      className="benefit-highlight"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '100%',
                        width: '5px',
                        backgroundColor: '#edb92e',
                        opacity: 0.8,
                        transition: 'all 0.3s ease'
                      }}
                    />
                    
                    <Box sx={{ 
                      display: 'flex', 
                      p: 3
                    }}>
                      <Box 
                        className="benefit-icon-wrapper"
                        sx={{ 
                          mr: 3, 
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '70px',
                          height: '70px',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(237, 185, 46, 0.1)',
                          color: '#edb92e',
                          transition: 'all 0.3s ease',
                          flexShrink: 0
                        }}
                      >
                        {benefit.icon}
                      </Box>
                      
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: '#edb92e', 
                            fontWeight: 600,
                            mb: 1.5 
                          }}
                        >
                          {benefit.title}
                        </Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: 'rgba(255, 255, 255, 0.8)',
                            fontSize: '0.95rem',
                            lineHeight: 1.5
                          }}
                        >
                          {benefit.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body1" sx={{ 
                color: 'rgba(255, 255, 255, 0.9)', 
                fontSize: '1.1rem',
                lineHeight: 1.7,
                mb: 4
              }}>
                Transform your manufacturing operations with spatial intelligence that reveals the hidden story 
                of your production floor. Our 3D analysis shows you exactly where time is lost, where workers 
                struggle, and where your biggest opportunities for improvement lie.
              </Typography>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: '#edb92e' 
                    }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Bottleneck Detection
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: '#1dd1a1' 
                    }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Workflow Optimization
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: '#eb4d4b' 
                    }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Safety Risk Zones
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: '#9b59b6' 
                    }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Efficiency Heat Maps
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* Capability Statement */}
        <Box sx={{ mb: 6 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h4" sx={{ color: 'white', mb: 3, fontWeight: 600 }}>
                Platform Capabilities
              </Typography>
              <Typography variant="body1" sx={{ 
                color: 'rgba(255, 255, 255, 0.8)', 
                fontSize: '1.1rem',
                lineHeight: 1.8,
                mb: 4
              }}>
                We drive automated action from visual data.
                <br /><br />
                Non-value-add time, is a bottleneck in industrial productivity.
                Companies are committed to improving productivity and safety, but lacks fine-grained understanding on the root causes of <strong>waiting</strong> and <strong>excess motion</strong>.
                <br /><br />
                Supervisors measure these wastes manually (and sparsely) using job cards.
                <br /><br />
                Our perception technologies can drive bottom-line improvement to industrial operations by visually inferring the root causes of time-in-motion and waiting wastes.
                <br /><br />
                Enhance manufacturing decision-making with visual data.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              {/* Visual data section - leaving this Grid item for layout balance */}
            </Grid>
            {/* Lean Manufacturing Wastes */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <img 
                src="/images/lean_manufacturing_wastes.jpg" 
                alt="Lean Manufacturing Wastes" 
                style={{ 
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '8px'
                }} 
              />
            </Box>
          </Grid>
        </Box>

        {/* Labor Cost Optimization Section */}
        <Box sx={{ mb: 6 }}>
          <Paper sx={{ 
            p: 6, 
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(237, 185, 46, 0.3)'
          }}>
            <Typography variant="h4" sx={{ color: '#edb92e', mb: 4, fontWeight: 600, textAlign: 'center' }}>
              Labour Cost Optimization Through Spatial Intelligence
            </Typography>
            
            <Grid container spacing={4} alignItems="center" sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" sx={{ 
                  color: 'rgba(255, 255, 255, 0.8)', 
                  fontSize: '1.1rem',
                  lineHeight: 1.7,
                  mb: 3
                }}>
                  Labour costs represent a large proportion of total manufacturing expenses. Even modest improvements 
                  in workforce efficiency can deliver a margin enhancement.

                  Much of the total production time is non-task-time, such as repositioning, reloading tools, and waiting for upstream processes.
                </Typography>
                
                <Box sx={{ backgroundColor: 'rgba(237, 185, 46, 0.1)', p: 3, borderRadius: 2, mb: 3 }}>
                  <Typography variant="h6" sx={{ color: '#edb92e', mb: 2, fontWeight: 600 }}>
                    Quantified Labor Insights
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, color: 'rgba(255, 255, 255, 0.8)' }}>
                    <li>Measure actual vs. planned cycle times at subtask granularity</li>
                    <li>Identify process bottlenecks and suboptimal work design</li>
                    <li>Quantify time lost to inefficient movement patterns and layout issues</li>
                    <li>Track productivity variations across shifts and scenarios</li>
                    <li>Real-time comprehension of cost per unit</li>
                  </Box>

                </Box>
                <ManufacturingFloorVisualization />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 2, textAlign: 'center' }}>
                MotionLogic® identifies productivity opportunities and key safety concerns on the manufacturing floor
              </Typography>
              </Grid>
              
            </Grid>
          </Paper>
        </Box>

        {/* Manufacturing Floor Visualization */}
        {/* <Box sx={{ 
          my: 8,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(100, 180, 255, 0.1) 0%, rgba(80, 120, 180, 0.1) 100%)',
          border: '1px solid rgba(29, 209, 161, 0.3)',
          p: 4
        }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 3, textAlign: 'center' }}>
            Manufacturing Floor Analysis
          </Typography>
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 2
          }}>
          </Box>
        </Box> */}



        {/* Video Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Paper sx={{ 
            p: 4, 
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <Typography variant="h4" sx={{ color: 'white', mb: 4, fontWeight: 600 }}>
              See MotionLogic® in Action
            </Typography>
            <Box sx={{ 
              position: 'relative',
              paddingBottom: '56.25%' // 16:9 aspect ratio
            }}>
              <iframe 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}
                src="https://www.youtube.com/embed/1stQbXi217I?start=3&autoplay=0&loop=1&mute=0&rel=0" 
                title="MotionLogic Manufacturing Demo" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                allowFullScreen
              />
            </Box>
          </Paper>
        </Box>

        {/* Call to Action */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Paper sx={{ 
            p: 6, 
            backgroundColor: 'rgba(237, 185, 46, 0.1)',
            border: '1px solid rgba(237, 185, 46, 0.3)'
          }}>
            <Typography variant="h4" sx={{ color: 'white', mb: 3, fontWeight: 600 }}>
              Ready to Transform Your Manufacturing?
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              mb: 4,
              fontSize: '1.1rem'
            }}>
              Want to achieve operational excellence with data-driven industrial automation? Book a no obligation call to learn more about our manufacturing solutions.
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              <Grid item>
                <Button 
                  variant="contained" 
                  size="large"
                  sx={{ 
                    backgroundColor: '#edb92e',
                    color: '#24242a',
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#d4a326'
                    }
                  }}
                  href="/contact"
                >
                  Learn More
                </Button>
              </Grid>
              <Grid item>
                <Button 
                  variant="outlined" 
                  size="large"
                  sx={{ 
                    borderColor: '#edb92e',
                    color: '#edb92e',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: '#d4a326',
                      backgroundColor: 'rgba(237, 185, 46, 0.1)'
                    }
                  }}
                  href="/contact"
                >
                  Book A Consult
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Manufacturing;
